/**
 * The draw being composed on /randomizer, before anything is sent to the API.
 *
 * Exclusions are keyed by the name as typed, but every comparison goes through
 * normalizeName — the API is case-insensitive about who is who, and the editor
 * has to agree with it or a "mutual" toggle silently does nothing.
 */

import {
  DEFAULT_ORGANIZER_USERNAME,
  QUICK_BOX_MIN_PARTICIPANTS,
  QuickBoxCreateInput,
  randomize,
} from '@lub0v/santa-utils';
import { normalizeName } from '@src/components/NamesInput/parseNames';

export type DrawDraft = {
  gameName: string;
  names: string[];
  /** name as typed → the names they must not give to. */
  exclusionsByName: Record<string, string[]>;
};

export function emptyDraft(gameName = ''): DrawDraft {
  return { gameName, names: [], exclusionsByName: {} };
}

export function excludedFor(draft: DrawDraft, name: string): string[] {
  return draft.exclusionsByName[name] || [];
}

export function hasAnyExclusion(draft: DrawDraft): boolean {
  return Object.values(draft.exclusionsByName).some((names) => names.length > 0);
}

export function clearExclusions(draft: DrawDraft): DrawDraft {
  return { ...draft, exclusionsByName: {} };
}

/** Replace one person's list, dropping blanks, self-references and repeats. */
export function setExclusionsFor(draft: DrawDraft, name: string, excluded: string[]): DrawDraft {
  const self = normalizeName(name);
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const item of excluded) {
    const key = normalizeName(item);
    if (!key || key === self || seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(item);
  }
  return {
    ...draft,
    exclusionsByName: { ...draft.exclusionsByName, [name]: unique },
  };
}

/** True when `to` also refuses `from` — the pair is excluded both ways. */
export function isMutual(draft: DrawDraft, from: string, to: string): boolean {
  return excludedFor(draft, to).some((name) => normalizeName(name) === normalizeName(from));
}

/** Add `to` to `from`'s list, and mirror it back when mutual is on. */
export function setMutual(draft: DrawDraft, from: string, to: string, mutual: boolean): DrawDraft {
  const next = setExclusionsFor(draft, from, [
    ...excludedFor(draft, from).filter((name) => normalizeName(name) !== normalizeName(to)),
    to,
  ]);
  const reverse = excludedFor(next, to);
  const mirrored = reverse.some((name) => normalizeName(name) === normalizeName(from));
  if (mutual && !mirrored) {
    return setExclusionsFor(next, to, [...reverse, from]);
  }
  if (!mutual && mirrored) {
    return setExclusionsFor(
      next,
      to,
      reverse.filter((name) => normalizeName(name) !== normalizeName(from)),
    );
  }
  return next;
}

/** Drop a name and every reference to it. Used when the list is edited again. */
export function pruneExclusions(draft: DrawDraft): DrawDraft {
  const known = new Set(draft.names.map(normalizeName));
  const exclusionsByName: Record<string, string[]> = {};
  for (const name of draft.names) {
    const kept = excludedFor(draft, name).filter((item) => known.has(normalizeName(item)));
    if (kept.length > 0) {
      exclusionsByName[name] = kept;
    }
  }
  return { ...draft, exclusionsByName };
}

/**
 * True when no assignment satisfies the exclusions. Runs the same `randomize`
 * the API runs, so the answer here is the answer there — the difference is that
 * here it costs nothing and we can say so before the draw is submitted.
 */
export function isDrawImpossible(draft: DrawDraft, circleDraw = false): boolean {
  const names = draft.names.map((name) => name.trim()).filter(Boolean);
  if (names.length < QUICK_BOX_MIN_PARTICIPANTS) {
    return false;
  }
  const idByName = new Map(names.map((name, index) => [normalizeName(name), String(index + 1)]));
  try {
    randomize(
      names.map((name, index) => ({
        id: String(index + 1),
        exclusions: excludedFor(draft, name)
          .map((excluded) => idByName.get(normalizeName(excluded)))
          .filter((id): id is string => Boolean(id)),
      })),
      circleDraw,
    );
    return false;
  } catch {
    return true;
  }
}

export function draftToCreateInput(draft: DrawDraft): QuickBoxCreateInput {
  const input = new QuickBoxCreateInput();
  input.name = draft.gameName.trim();
  input.organizerName = DEFAULT_ORGANIZER_USERNAME;
  input.participants = draft.names.map((username) => ({
    username,
    excludedUsernames: excludedFor(draft, username),
  }));
  return input;
}
